import { useMemo } from 'react';

import { CellActions } from 'components/TableCells/CellActions';
import { CellActionsHeader } from 'components/TableCells/CellActionsHeader';
import { CellCheck } from 'components/TableCells/CellCheck';
import { CellCurrency } from 'components/TableCells/CellCurrency';
import { CellDate } from 'components/TableCells/CellDate';
import { CellHeader } from 'components/TableCells/CellHeader';
import { CellPerson } from 'components/TableCells/CellPerson';
import { CellStatus } from 'components/TableCells/CellStatus';
import { CellText } from 'components/TableCells/CellText';
import { CellTripLink } from 'components/TableCells/CellTripLink';

import { i18n } from '../../TableTransactions.i18n';
import { useMenuOptions } from '../useMenuOptions';

import { StatusToColor, UseColumns } from './useColumns.types';

const statusToColor: StatusToColor = {
    paid: 'default',
    verified: 'blue',
    completed: 'green',
    hold: 'blue',
};

export const useColumns: UseColumns = actionsCallbacks => {
    return useMemo(() => [
        {
            accessor: 'trip_id',
            Header: props => <CellHeader {...props} value={i18n('trip_id_header')} />,
            Cell: props => {
                const {
                    value: tripId,
                    row: { original: transaction },
                } = props;

                const { person: { person_id } } = transaction;

                return (
                    <CellTripLink
                        {...props}
                        personId={person_id}
                        tripId={tripId}
                    />
                );
            },
            sticky: 'left',
        },
        {
            id: 'person',
            accessor: ({
                person: {
                    login,
                    first_name: firstName,
                    last_name: lastName,
                    person_id: personId,
                },
            }) => ({
                login,
                firstName,
                lastName,
                personId,
            }),
            Header: props => <CellHeader {...props} value={i18n('person_header')} />,
            Cell: CellPerson,
            width: 260,
        },
        {
            id: 'service_type',
            accessor: ({ service_type }) => i18n(`service_type_value_${service_type}`),
            Header: props => <CellHeader {...props} value={i18n('service_type_header')} />,
            Cell: CellText,
        },
        {
            id: 'execution_date',
            accessor: ({ execution_date }) => new Date(execution_date),
            Header: props => <CellHeader {...props} value={i18n('execution_date_header')} />,
            Cell: CellDate,
        },
        {
            id: 'type',
            accessor: ({ type }) => i18n(`type_value_${type}`),
            Header: props => <CellHeader {...props} value={i18n('type_header')} />,
            Cell: CellText,
        },
        {
            accessor: 'is_penalty',
            Header: props => <CellHeader {...props} value={i18n('is_penalty_header')} />,
            Cell: CellCheck,
            width: 80,
        },
        {
            id: 'cost_center',
            accessor: ({ cost_center }) => cost_center?.name,
            Header: props => <CellHeader {...props} value={i18n('cost_center_header')} />,
            Cell: CellText,
        },
        {
            id: 'is_offline',
            accessor: ({ is_offline }) => i18n(`is_offline_value_${is_offline}`),
            Header: props => <CellHeader {...props} value={i18n('is_offline_header')} />,
            Cell: CellText,
        },
        {
            accessor: 'price',
            Header: props => <CellHeader {...props} align="right" value={i18n('price_header')} />,
            Cell: CellCurrency,
        },
        {
            accessor: 'fee',
            Header: props => <CellHeader {...props} align="right" value={i18n('fee_header')} />,
            Cell: CellCurrency,
        },
        {
            accessor: 'full_price',
            Header: props => <CellHeader {...props} align="right" value={i18n('full_price_header')} />,
            Cell: CellCurrency,
        },
        {
            id: 'invoice_date',
            accessor: ({ invoice_date }) => invoice_date ? new Date(invoice_date) : null,
            Header: props => <CellHeader {...props} value={i18n('invoice_date_header')} />,
            Cell: CellDate,
        },
        {
            id: 'status',
            accessor: ({ status }) => ({ text: i18n(`status_value_${status}`), color: statusToColor[status] }),
            Header: props => <CellHeader {...props} value={i18n('status_header')} />,
            Cell: CellStatus,
            width: 180,
        },
        {
            accessor: 'general_service_id',
            Header: props => <CellHeader {...props} value={i18n('general_service_id_header')} />,
            Cell: CellText,
        },
        {
            accessor: 'actions',
            Header: CellActionsHeader,
            Cell: props => {
                const {
                    value: rawActions,
                    row: { original: transaction },
                } = props;

                const actions = rawActions || { update: false, delete: false, set_transaction_paid: false };
                const menuOptions = useMenuOptions(actions, actionsCallbacks, transaction);

                return (
                    <CellActions
                        {...props}
                        menuOptions={menuOptions}
                    />
                );
            },
            width: 48,
            sticky: 'right',
        },
    ], [actionsCallbacks]);
};
